import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Lock, Check } from "lucide-react";
import { toast } from "sonner";
import MobileLayout from "@/components/MobileLayout";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";

/**
 * /reset-password — landing page from the Supabase recovery email link.
 *
 * Flow:
 *   1. User clicks the email link → Supabase auth gateway exchanges the
 *      recovery token and lands here with a recovery session attached
 *      (Supabase Auth v2: the SDK fires onAuthStateChange("PASSWORD_RECOVERY")
 *      once on landing; it ALSO writes the session, so getSession() returns
 *      a row in that window).
 *   2. We listen for that event AND check getSession() on mount to cover
 *      both ordering races.
 *   3. User submits a new password → supabase.auth.updateUser({ password }).
 *   4. Success → toast + navigate("/auth?mode=signin") so they sign in with the new pwd.
 *
 * This page must be in LanguageGate.PUBLIC_BYPASS — the user may arrive
 * from the email link without ever having set a language cookie on this
 * device.
 *
 * REDIRECT URL ALLOWLIST: the Supabase project's Authentication → URL
 * Configuration → Redirect URLs MUST include
 * https://www.aivyv.com/reset-password (and any preview domain) or the
 * email link comes back as an error.
 */
const ResetPassword = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  // recovery: we've confirmed a recovery session is in place (or one
  // already exists when the page mounted via direct nav while logged in).
  // ready: initial probe finished — if false we render the spinner.
  const [ready, setReady] = useState(false);
  const [recovery, setRecovery] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Probe current session synchronously. Two cases land here with a
    // valid session: (a) the email link just exchanged the recovery
    // token, (b) a logged-in user typed the URL directly. Both let
    // updateUser({ password }) succeed — we don't need to require
    // PASSWORD_RECOVERY specifically.
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      if (data?.session?.user) setRecovery(true);
      setReady(true);
    });

    // Belt-and-suspenders: Supabase fires PASSWORD_RECOVERY shortly after
    // the recovery-link exchange. We flip recovery=true here too so the
    // form unlocks the moment the session lands, even if getSession()
    // beat the exchange by a few ms above.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") {
        if (session?.user) setRecovery(true);
        setReady(true);
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(t("auth.reset.error.tooShort"));
      return;
    }
    if (password !== confirm) {
      toast.error(t("auth.reset.error.mismatch"));
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setDone(true);
      toast.success(t("auth.reset.success.toast"));
      // Sign out the recovery session so the user has to type the new
      // password explicitly. Avoids the "I see /home but didn't really
      // log in" confusion.
      await supabase.auth.signOut();
      setTimeout(() => navigate("/auth?mode=signin"), 1400);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? "";
      toast.error(msg || t("auth.reset.error.generic"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MobileLayout className="justify-center py-12 relative">
      <motion.div
        className="flex flex-col items-center gap-8 w-full"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center space-y-2">
          <div
            className="w-12 h-12 rounded-2xl mx-auto flex items-center justify-center"
            style={{
              background: "hsl(187 72% 50% / 0.10)",
              border: "1px solid hsl(187 72% 50% / 0.35)",
              boxShadow: "0 0 20px hsl(187 72% 50% / 0.18)",
            }}
          >
            {done ? (
              <Check size={20} className="text-primary" />
            ) : (
              <Lock size={20} className="text-primary" />
            )}
          </div>
          <h2 className="font-display text-2xl font-semibold text-foreground">
            {done ? t("auth.reset.success.title") : t("auth.reset.title")}
          </h2>
          <p className="text-muted-foreground text-sm">
            {done ? t("auth.reset.success.body") : t("auth.reset.body")}
          </p>
        </div>

        {!ready ? (
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        ) : !recovery ? (
          <div className="text-center max-w-xs">
            <p className="text-[13px] font-body leading-relaxed text-foreground/70 mb-4">
              {t("auth.reset.invalidLink")}
            </p>
            <Button
              variant="hero"
              size="lg"
              className="w-full"
              onClick={() => navigate("/auth?mode=signin")}
            >
              {t("auth.reset.backToLogin")}
            </Button>
          </div>
        ) : done ? (
          <p className="text-[12px] font-body text-muted-foreground">
            {t("auth.reset.redirecting")}
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="w-full space-y-3">
            <Input
              type="password"
              placeholder={t("auth.reset.newPassword")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="bg-secondary/60 border-border/50 text-foreground placeholder:text-muted-foreground h-13 rounded-xl text-sm"
            />
            <Input
              type="password"
              placeholder={t("auth.reset.confirmPassword")}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
              className="bg-secondary/60 border-border/50 text-foreground placeholder:text-muted-foreground h-13 rounded-xl text-sm"
            />
            <Button
              variant="hero"
              size="xl"
              type="submit"
              className="w-full"
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("auth.reset.submit")
              )}
            </Button>
          </form>
        )}
      </motion.div>
      <Footer variant="minimal" />
    </MobileLayout>
  );
};

export default ResetPassword;
